import { useState, useEffect, useMemo } from "react";
import { AlertCircle } from "lucide-react";
import { AffiliationList, type AffiliationEntry } from "../../components/AffiliationList";
import type { OptionType } from "@themes/pollinator/ui/Select";
import { ActionButtonsGroup } from "@themes/pollinator/ui/ActionButtonsGroup";
import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";

export type { AffiliationEntry };

export type Props = {
  /** Current affiliations value */
  affiliations: AffiliationEntry[];
  /** Handler when affiliations change */
  onAffiliationsChange: (value: AffiliationEntry[]) => void;
  /** Organization options for select */
  organizationOptions: OptionType[];
  /** Role options for select */
  roleOptions: OptionType[];
  /** Whether options are loading */
  isOptionsLoading?: boolean;
  /** Handler for creating new organization */
  onCreateOrganization?: (name: string) => void;
  /** Handler for save button */
  onSave: () => void;
  /** Handler for cancel button */
  onCancel: () => void;
  /** Whether the form has unsaved changes */
  isDirty: boolean;
  /** Whether the form is submitting */
  isSubmitting: boolean;
  /** Error message to display */
  error?: string | null;
  /** Whether any entry has an organization but no role */
  hasIncompleteEntries?: boolean;
  /** Handler for delete button click on a row (opens confirmation dialog) */
  onDeleteEntry?: (index: number) => void;
};

/**
 * Affiliations form component.
 * Allows users to manage their organization affiliations.
 * This is a presentational component - business logic is in the container.
 */
export function AffiliationsForm({
  affiliations,
  onAffiliationsChange,
  organizationOptions,
  roleOptions,
  isOptionsLoading = false,
  onCreateOrganization,
  onSave,
  onCancel,
  isDirty,
  isSubmitting,
  error,
  hasIncompleteEntries = false,
  onDeleteEntry,
}: Props) {
  // Count filled affiliations (both org and role selected)
  const filledCount = useMemo(
    () => affiliations.filter((a) => a.organization && a.role).length,
    [affiliations]
  );

  // Start with filled count + 1 empty row (minimum 1)
  const [displayedRows, setDisplayedRows] = useState(
    Math.max(1, filledCount + 1)
  );

  // Update displayed rows when affiliations change (e.g., on initial load)
  useEffect(() => {
    const newMinRows = Math.max(1, filledCount + 1);
    if (newMinRows > displayedRows) {
      setDisplayedRows(newMinRows);
    }
  }, [filledCount, displayedRows]);

  const handleAddAnother = () => {
    setDisplayedRows((prev) => prev + 1);
  };

  return (
    <div className="flex flex-col gap-4" data-testid="affiliations-form">
      {error && (
        <div className="flex flex-col gap-2 border-2 border-error rounded-md p-4">
          <div className="flex flow-row items-center gap-2">
            <AlertCircle className="h-5 w-5 text-error" />
            <Type variant="h6" className="text-error">
              Something went wrong
            </Type>
          </div>
          <Type variant="caption" className="text-error">
            {error}
          </Type>
        </div>
      )}
      <AffiliationList
        name="affiliations"
        value={affiliations}
        onChange={onAffiliationsChange}
        organizationOptions={organizationOptions}
        roleOptions={roleOptions}
        isLoading={isOptionsLoading}
        onCreateOrganization={onCreateOrganization}
        disabled={isSubmitting}
        maxItems={displayedRows}
        allowDelete={!!onDeleteEntry}
        onDeleteEntry={onDeleteEntry}
      />
      <Button
        variant="link"
        onClick={handleAddAnother}
        disabled={isSubmitting}
        className="self-start"
      >
        + Add Another Affiliation
      </Button>
      {hasIncompleteEntries && (
        <Type variant="caption" className="text-error">
          Please select a role for all affiliations before saving.
        </Type>
      )}
      <ActionButtonsGroup
        primaryButtonText="Save Affiliations"
        onPrimaryClick={onSave}
        secondaryButtonText={isDirty ? "Cancel" : undefined}
        onSecondaryClick={isDirty ? onCancel : undefined}
        className="w-fit self-end"
        primaryButtonProps={{
          disabled: !isDirty || isSubmitting || hasIncompleteEntries,
        }}
      />
    </div>
  );
}

export default AffiliationsForm;
